import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, ArrowLeft, RefreshCw, Loader2 } from "lucide-react";
import logoFull from "@/assets/logo-full.png";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function CheckEmail() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "your email";
  const { toast } = useToast();
  
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown timer for cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (resending || resendCooldown > 0) return;
    
    setResending(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("resend-verification-email", {
        body: { email },
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        if (data.error === "Email is already verified") {
          toast({
            title: "Already Verified",
            description: "Your email is already verified. You can sign in now.",
          });
          // Redirect to auth page
          window.location.href = "/auth";
          return;
        }
        throw new Error(data.error);
      }

      toast({
        title: "Email Sent",
        description: "A new verification email has been sent. Please check your inbox.",
      });
      
      // Start 60-second cooldown
      setResendCooldown(60);
    } catch (error: any) {
      console.error("Error resending verification email:", error);
      toast({
        title: "Failed to Resend",
        description: error.message || "Unable to send verification email. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  const handleBackToSignIn = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.log("Sign out error (continuing anyway):", error);
    }
    // Always redirect, even if signOut fails
    window.location.href = '/auth';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <Card className="w-full max-w-md shadow-elevated">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoFull} alt="PTA Hub" className="h-12" />
          </div>
          <div className="flex justify-center mb-2">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle>Check Your Email</CardTitle>
          <CardDescription className="text-base">
            We've sent a verification link to
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted rounded-lg p-3 text-center">
            <p className="font-medium text-foreground break-all">{email}</p>
          </div>
          
          <div className="space-y-3 text-center text-sm text-muted-foreground">
            <p>
              Click the link in the email to verify your account. The link will expire in 24 hours.
            </p>
            <p>
              Once verified, you'll be able to log in and complete setting up your PTA.
            </p>
          </div>

          <div className="border-t pt-4 space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Didn't receive the email? Check your spam folder or try resending.
            </p>
            
            <Button 
              variant="secondary" 
              className="w-full"
              onClick={handleResendEmail}
              disabled={resending || resendCooldown > 0}
            >
              {resending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend in {resendCooldown}s
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend Verification Email
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleBackToSignIn}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sign In
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              Still having issues? Contact{" "}
              <a href="mailto:info@ptahub.co.uk" className="text-primary hover:underline">
                info@ptahub.co.uk
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
